import { Link, useNavigate } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";

const COMPLIANCE_LINKS = [
  { label: "Accounting Services", route: "/accounting" },
  { label: "GST Filing", route: "/gst-filing" },
  { label: "Income Tax", route: "/income-tax" },
  { label: "MCA Compliance", route: "/mca-compliance" },
];

const IT_LINKS = [
  { label: "Managed IT", route: "/managed-it" },
  { label: "Cybersecurity", route: "/cybersecurity" },
  { label: "Cloud Computing", route: "/cloud-computing" },
  { label: "IT Consulting", route: "/it-consulting" },
  { label: "Software Dev", route: "/software-development" },
];

const SUPPLY_LINKS = [
  { label: "Office Supplies", route: "/office-supplies" },
  { label: "Fresh Fruits", route: "/fresh-fruits" },
  { label: "Corporate Gifts", route: "/corporate-gifts" },
  { label: "Branded Apparel", route: "/branded-apparel" },
];

const MANPOWER_LINKS = [
  { label: "Permanent Staffing", route: "/permanent-staffing" },
  { label: "Payroll Mgmt", route: "/payroll-management" },
  { label: "Facility Mgmt", route: "/facility-management" },
  { label: "Executive Search", route: "/executive-search" },
];

const COMPANY_LINKS = [
  { label: "About Us", route: "/about" },
  { label: "Partner With Us", route: "/partner" },
  { label: "Privacy Policy", route: "/privacy-policy" },
  { label: "Terms of Service", route: "/terms" },
  { label: "Refund Policy", route: "/refund-policy" },
];

function FooterLinkGroup({ title, links }) {
  const navigate = useNavigate();
  return (
    <div>
      <h4 className="text-white font-extrabold text-[11px] uppercase tracking-[0.18em] mb-2 md:mb-3 font-body">
        {title}
      </h4>
      <ul className="space-y-1 md:space-y-1.5">
        {links.map((l) => (
          <li key={l.label}>
            <button
              onClick={() => {
                navigate(l.route);
                window.scrollTo(0, 0);
              }}
              className="text-slate-300 hover:text-red-400 text-[13px] font-semibold transition-colors flex items-center gap-1.5 font-body"
            >
              <span className="text-slate-500 text-[10px]">&rsaquo;</span>
              {l.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompanyLink({ label, route }) {
  const navigate = useNavigate();
  return (
    <li>
      <button
        onClick={() => { navigate(route); window.scrollTo(0, 0); }}
        className="text-slate-300 hover:text-red-400 text-[13px] font-semibold transition-colors flex items-center gap-1.5 font-body"
      >
        <span className="text-slate-500 text-[10px]">&rsaquo;</span>
        {label}
      </button>
    </li>
  );
}

export function Footer({ onOpenAppointment }) {
  return (
    <footer data-testid="main-footer" className="bg-gradient-to-b from-slate-950 to-slate-900 pt-14 pb-24 md:pb-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-7 md:p-9 flex flex-col md:flex-row items-center justify-between mb-14 shadow-xl shadow-red-900/20 gap-5">
          <div>
            <p className="text-red-200 text-xs font-bold tracking-[0.15em] uppercase mb-1 font-body">
              Ready to Switch?
            </p>
            <h3 className="text-white text-xl md:text-2xl font-black font-heading">
              Get a free consultation today
            </h3>
          </div>
          <button
            data-testid="footer-book-appointment-btn"
            onClick={onOpenAppointment}
            className="bg-white text-red-600 font-extrabold text-sm px-6 py-3 rounded-xl hover:shadow-lg transition-all active:scale-95 font-body whitespace-nowrap"
          >
            Book Your Appointment
          </button>
        </div>

        {/* Footer Grid - Optimized for mobile */}
        <div className="mb-8 md:mb-12">
          
          {/* MOBILE LAYOUT - Space Optimized */}
          <div className="md:hidden space-y-6">
            {/* Section 1: Logo + Contact + Address (2 lines) */}
            <div>
              <Link to="/" className="inline-flex items-center gap-3 mb-4 no-underline" onClick={() => window.scrollTo(0, 0)}>
                <div className="w-11 h-11 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/40 flex-shrink-0">
                  <span className="text-white font-black text-lg">S</span>
                </div>
                <div>
                  <p className="text-white font-black text-[15px] leading-none font-body">SwitchYourVendor</p>
                  <p className="text-slate-400 text-[11px] font-semibold mt-1 font-body">Stop Settling. Start Switching.</p>
                </div>
              </Link>
              <p className="text-slate-300 text-[13px] leading-relaxed mb-3 font-body font-medium">
                India's #1 one-stop vendor platform for SMEs — compliance, IT, supplies, and manpower.
              </p>
              <div className="space-y-2">
                <a href="tel:+918287428256" className="text-white font-bold text-[13px] flex items-center gap-3 font-body hover:text-red-400 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                    <Phone size={15} className="text-red-400" />
                  </div>
                  <span className="whitespace-nowrap">+91 8287428256</span>
                </a>
                <a href="mailto:info@switchyourvendor.com" className="text-white font-bold text-[13px] flex items-center gap-3 font-body hover:text-red-400 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                    <Mail size={15} className="text-red-400" />
                  </div>
                  <span className="break-all">info@switchyourvendor.com</span>
                </a>
                <div className="text-slate-200 font-semibold text-[12px] flex items-start gap-3 font-body leading-relaxed">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={15} className="text-red-400" />
                  </div>
                  <span>
                    1st Floor, U12, Nearby CyberPark, Sector 24<br />
                    Gurugram, Haryana 122002
                  </span>
                </div>
              </div>
            </div>

            {/* Section 2: Services in 2x2 Grid */}
            <div className="grid grid-cols-2 gap-5">
              {/* Row 1: Supplies | Compliance */}
              <FooterLinkGroup title="Supplies" links={SUPPLY_LINKS} />
              <FooterLinkGroup title="Compliance" links={COMPLIANCE_LINKS} />
              
              {/* Row 2: IT Services | Manpower */}
              <FooterLinkGroup title="IT Services" links={IT_LINKS} />
              <FooterLinkGroup title="Manpower" links={MANPOWER_LINKS} />
            </div>
          </div>

          {/* DESKTOP LAYOUT - 5 columns (unchanged) */}
          <div className="hidden md:grid md:grid-cols-5 gap-8">
            {/* Column 1: Brand + Contact - spans 2 columns */}
            <div className="md:col-span-2">
              <Link to="/" className="inline-flex items-center gap-3 mb-5 no-underline" onClick={() => window.scrollTo(0, 0)}>
                <div className="w-11 h-11 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-lg shadow-red-600/40 flex-shrink-0">
                  <span className="text-white font-black text-lg">S</span>
                </div>
                <div>
                  <p className="text-white font-black text-[15px] leading-none font-body">SwitchYourVendor</p>
                  <p className="text-slate-400 text-[11px] font-semibold mt-1 font-body">Stop Settling. Start Switching.</p>
                </div>
              </Link>
              <p className="text-slate-300 text-[13px] leading-relaxed mb-5 font-body font-medium">
                India's #1 one-stop vendor platform for SMEs — compliance, IT, supplies, and manpower.
              </p>
              <div className="space-y-3">
                <a href="tel:+918287428256" className="text-white font-bold text-[13px] flex items-center gap-3 font-body hover:text-red-400 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                    <Phone size={15} className="text-red-400" />
                  </div>
                  <span className="whitespace-nowrap">+91 8287428256</span>
                </a>
                <a href="mailto:info@switchyourvendor.com" className="text-white font-bold text-[13px] flex items-center gap-3 font-body hover:text-red-400 transition-colors break-all sm:break-normal">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0">
                    <Mail size={15} className="text-red-400" />
                  </div>
                  <span>info@switchyourvendor.com</span>
                </a>
                <div className="text-slate-200 font-semibold text-[12px] flex items-start gap-3 font-body leading-relaxed">
                  <div className="w-8 h-8 rounded-lg bg-white/[0.08] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin size={15} className="text-red-400" />
                  </div>
                  <span>
                    1st Floor, U12, Nearby CyberPark, Sector 24<br />
                    Gurugram, Haryana 122002
                  </span>
                </div>
              </div>
            </div>

            {/* Column 2: Company */}
            <div>
              <h4 className="text-white font-extrabold text-[11px] uppercase tracking-[0.18em] mb-3 font-body">Company</h4>
              <ul className="space-y-1.5">
                {COMPANY_LINKS.map((l) => (
                  <CompanyLink key={l.label} label={l.label} route={l.route} />
                ))}
              </ul>
            </div>

            {/* Column 3: Compliance + Supplies */}
            <div className="space-y-8">
              <FooterLinkGroup title="Compliance" links={COMPLIANCE_LINKS} />
              <FooterLinkGroup title="Supplies" links={SUPPLY_LINKS} />
            </div>

            {/* Column 4: IT Services + Manpower */}
            <div className="space-y-8">
              <FooterLinkGroup title="IT Services" links={IT_LINKS} />
              <FooterLinkGroup title="Manpower" links={MANPOWER_LINKS} />
            </div>
          </div>
        </div>

        {/* Bottom bar - Mobile: Copyright + Company links, Desktop: Copyright only */}
        <div className="border-t border-slate-800 pt-5">
          <p className="text-slate-400 text-xs font-semibold font-body text-center md:text-left mb-4 md:mb-0">
            &copy; 2025 SwitchYourVendor. All rights reserved.
          </p>
          
          {/* Mobile Company Links - Below copyright - Evenly distributed 2-column grid */}
          <div className="md:hidden grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
            <Link
              to="/about"
              className="text-slate-400 hover:text-red-400 text-xs font-semibold transition-colors font-body text-center"
              onClick={() => window.scrollTo(0, 0)}
            >
              About Us
            </Link>
            <Link
              to="/partner"
              className="text-slate-400 hover:text-red-400 text-xs font-semibold transition-colors font-body text-center"
              onClick={() => window.scrollTo(0, 0)}
            >
              Partner With Us
            </Link>
            <Link
              to="/privacy-policy"
              className="text-slate-400 hover:text-red-400 text-xs font-semibold transition-colors font-body text-center"
              onClick={() => window.scrollTo(0, 0)}
            >
              Privacy Policy
            </Link>
            <Link
              to="/refund-policy"
              className="text-slate-400 hover:text-red-400 text-xs font-semibold transition-colors font-body text-center"
              onClick={() => window.scrollTo(0, 0)}
            >
              Refund Policy
            </Link>
            <Link
              to="/terms"
              className="text-slate-400 hover:text-red-400 text-xs font-semibold transition-colors font-body text-center col-span-2"
              onClick={() => window.scrollTo(0, 0)}
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
